// Starting a new site. The rest of the set-up (map, zones, wording and the ops
// tablet) continues from the site's page.

import { useState, type FormEvent } from 'react';
import { api, message } from '../api.ts';
import { Shell, useRoute } from '../ui.tsx';

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/\(.*?\)/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

export function NewSite() {
  const { go } = useRoute();
  const [clientName, setClient] = useState('');
  const [siteName, setSite] = useState('');
  const [timeZone, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [siteId, setId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = siteId || slug(siteName);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ siteId: string }>('POST', '/sites', { siteId: id, clientName, siteName, timeZone });
      go(`/sites/${res.siteId}/setup/map`);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <div className="head-title">
        <h1>New site</h1>
        <p className="muted">Set up with the client, usually on site. Nothing goes live until it's approved on the site's ops tablet.</p>
      </div>
      <form className="card" style={{ maxWidth: 820, gap: 18, padding: 28 }} onSubmit={submit}>
        <h2>Client and site</h2>
        <div className="form-row">
          <label className="field">
            Client
            <input className="input" value={clientName} onChange={(e) => setClient(e.target.value)} required autoFocus />
          </label>
          <label className="field">
            Site name
            <input className="input" value={siteName} onChange={(e) => setSite(e.target.value)} required />
          </label>
          <label className="field">
            Site time zone
            <input className="input" value={timeZone} onChange={(e) => setTz(e.target.value)} required />
          </label>
          <label className="field">
            Site ID (in the QR code; can't change later)
            <input className="input mono" value={id} onChange={(e) => setId(slug(e.target.value))} pattern="[a-z0-9][a-z0-9\-]{1,62}" required />
          </label>
        </div>
        <p className="faint small">Subscription: yearly, starting when the site goes live.</p>
        {error && <p className="error">{error}</p>}
        <div className="row">
          <button className="btn primary" type="submit" disabled={busy}>
            Create the site
          </button>
        </div>
      </form>
    </Shell>
  );
}
